import { createRouter, createWebHistory } from "vue-router";
import axios from 'axios';
import store from '../src/store/index.js';
import { resetAuthenticationForGuest } from '../src/services/guestSession.js';
import AuthMonitor from '../src/services/AuthMonitor.js';

//import website
import indexPage from "../src/views/index.vue";
import reservationPage from "../src/views/reservation.vue";
import searchPage from "../src/views/search.vue";
import loginPage from "../src/views/login.vue";
import registerPage from "../src/views/register.vue";
import whatsappPage from "../src/views/whatsapp.vue";
import updateDataPage from "../src/views/update_data.vue";
import updatePasswordPage from "../src/views/update_password.vue";
import contentsPage from "../src/views/contents.vue";
import examsPage from "../src/views/exams/index.vue";
import examDetailsPage from "../src/views/exams/show.vue";
import myExamsPage from "../src/views/my_exams/index.vue";
import myExamsDetailPage from "../src/views/my_exams/show.vue";
import privacyPolicyPage from "../src/views/privacy_policy.vue";
import termsOfServicePage from "../src/views/terms_of_service.vue";
import aboutPage from "../src/views/About.vue";
import blogListingPage from "../src/views/blog/BlogListing.vue";
import blogDetailPage from "../src/views/blog/BlogDetail.vue";
import qrProfilePage from "../src/views/QrProfile.vue";
import previewHomePage from "../src/views/preview/PreviewHome.vue";

// Subscriptions system (student)
import StoreListing from "../src/views/store/Listing.vue";
import ProductDetails from "../src/views/store/ProductDetails.vue";
import SubscribeWizard from "../src/views/subscriptions/Subscribe.vue";
import MySubscriptions from "../src/views/subscriptions/MySubscriptions.vue";
import PaymentHistory from "../src/views/subscriptions/PaymentHistory.vue";
import NotificationsCenter from "../src/views/subscriptions/Notifications.vue";
import SubscriptionRequired from "../src/views/subscriptions/SubscriptionRequired.vue";
import DeviceSuspended from "../src/views/device-suspended.vue";
import MySchedule from "../src/views/schedule/MySchedule.vue";
// import forgetPage from "../src/views/forget.vue";

// import NotFound from "../src/views/error.vue";


const routes = [
    { path: '/contents/:contentId(\\d+)', name: 'lessonPage', component: () => import('../src/views/LessonPage.vue'), meta: { requiresAuth: true } },
    { path: '/lessons/:lessonId/:contentId?', name: 'legacyLessonPage', component: () => import('../src/views/LessonPage.vue'), meta: { requiresAuth: true } },
    // Website routes
    { path: "/login", component: loginPage, name: "login", meta: { guest: true } },
    { path: "/register", component: registerPage, name: "register", meta: { forceGuest: true } },
    { path: "/privacy-policy", component: privacyPolicyPage, name: "privacyPolicy" },
    { path: "/terms-of-service", component: termsOfServicePage, name: "termsOfService" },

    // Homepage Builder (admin, portal) preview target — a dedicated route so
    // it can never inherit index.vue's auth-dependent branches. meta.isPreview
    // is checked first in the guard below, before requiresAuth/guest/
    // website_status, so it's never blocked by any of them.
    { path: "/__preview/home", component: previewHomePage, name: "previewHome", meta: { isPreview: true } },

    // Fix: was `meta: { requiresAuth: true }`, which sent every anonymous
    // visitor straight to /login before they ever saw index.vue's real
    // guest-facing content (HeroSection/QuickAccessSection/CtaSection all
    // have dedicated guest branches that were unreachable as a result). The
    // homepage is genuinely public now.
    { path: "/", component: indexPage, name: "index" },
    { path: "/reservation", component: reservationPage, name: "reservation", meta: { forceGuest: true } },
    { path: "/search", component: searchPage, name: "search", meta: { forceGuest: true } },
    { path: "/whatsapp_link", component: whatsappPage, name: "whatsapp_link", meta: { requiresAuth: true } },
    { path: "/contents", component: contentsPage, name: "contentsPage", meta: { requiresAuth: true } },
    { path: "/update_password", component: updatePasswordPage, name: "update_password", meta: { requiresAuth: true } },
    { path: "/update_data", component: updateDataPage, name: "update_data", meta: { requiresAuth: true } },
    { path: "/exams", component: examsPage, name: "exams", meta: { requiresAuth: true } },
    { path: "/schedule", component: MySchedule, name: "mySchedule", meta: { requiresAuth: true } },
    { path: "/exam/:id", component: examDetailsPage, name: "examDetailsPage", meta: { requiresAuth: true } },
    { path: "/my_exams", component: myExamsPage, name: "myExamsPage", meta: { requiresAuth: true } },
    { path: "/my_exam/:id", component: myExamsDetailPage, name: "myExamsDetailPage", meta: { requiresAuth: true } },
    // Public online-student self-registration. forceGuest (mirrors /reservation)
    // so the POST /register runs as a clean guest — the backend forces study_mode='online'.
    { path: "/about", component: aboutPage, name: "about" },
    { path: "/blogs", component: blogListingPage, name: "blogListing" },
    { path: "/blogs/:slug", component: blogDetailPage, name: "blogDetail" },
    // Student QR landing page — the URL encoded in every printed student QR
    // code (mobile.tarekmath.com/qr/{token}). Public, no auth required.
    { path: "/qr/:token", component: qrProfilePage, name: "qrProfile" },

    // ── Subscriptions storefront (public) ──
    { path: "/academic-years", component: StoreListing, name: "storeAcademicYears", props: { kind: "academic_year" }, meta: { requiresAuth: true } },
    { path: "/courses", component: StoreListing, name: "storeCourses", props: { kind: "course" }, meta: { requiresAuth: true } },
    { path: "/academic-year/:id", component: ProductDetails, name: "academicYearDetails", props: { kind: "academic_year" }, meta: { requiresAuth: true } },
    { path: "/course/:id", component: ProductDetails, name: "courseDetails", props: { kind: "course" }, meta: { requiresAuth: true } },
    { path: "/subscription-required", component: SubscriptionRequired, name: "subscriptionRequired", meta: { requiresAuth: true } },
    { path: "/device-suspended", component: DeviceSuspended, name: "deviceSuspended", meta: { requiresAuth: true } },

    // ── Subscriptions (auth) ──
    { path: "/subscribe", component: SubscribeWizard, name: "subscribe", meta: { requiresAuth: true } },
    { path: "/my-subscriptions", component: MySubscriptions, name: "mySubscriptions", meta: { requiresAuth: true } },
    { path: "/payment-history", component: PaymentHistory, name: "paymentHistory", meta: { requiresAuth: true } },
    { path: "/notifications", component: NotificationsCenter, name: "notifications", meta: { requiresAuth: true } },
    // { path: "/forget", component: forgetPage, name: "forget" },
    // { path: "/404", component: NotFound},
    // { path: "/:catchAll(.*)", redirect: "/404"},
];

const router = createRouter({
    routes,
    history: createWebHistory(),
    hashbang: false,
    mode: 'html5',
    linkActiveClass: "active",
    // linkExactActiveClass: "exact-active",
    scrollBehavior() {
        // Scroll to the top of the page
        return window.scrollTo({ top: 0, behavior: 'smooth' });
    },
});

// جلب حالة الموقع من localStorage أو من متغير جلوبال
router.beforeEach((to, from, next) => {
    // Homepage Builder preview route: must bypass every other guard below
    // (forceGuest/requiresAuth/guest/website_status) unconditionally — it's
    // meant to work even during maintenance mode, since the whole point is
    // previewing the configuration behind that gate.
    if (to.matched.some(record => record.meta.isPreview)) {
        next();
        return;
    }

    if (to.matched.some(record => record.meta.forceGuest)) {
        resetAuthenticationForGuest(store, axios);
    }

    const website_status = Number(localStorage.getItem('website_status')) || 0;

    // إذا الصفحة تتطلب تسجيل دخول
    if (to.matched.some(record => record.meta.requiresAuth)) {
        if (store.getters.isAuthenticated) {
            next();
        } else {
            next({ path: '/login', query: { redirect: to.fullPath } });
        }
        return;
    }

    // إذا الصفحة للزوار فقط
    if (to.matched.some(record => record.meta.guest)) {
        if (store.getters.isAuthenticated) {
            next('/');
        } else {
            next();
        }
        return;
    }

    else if (website_status === 0) {
        const publicPaths = ['/', '/reservation', '/register', '/search', '/login', '/whatsapp_link', '/privacy-policy', '/terms-of-service',
            '/academic-years', '/courses', '/subscription-required', '/device-suspended', '/about', '/blogs'];
        const publicPrefixes = ['/academic-year/', '/course/', '/blogs/', '/qr/'];
        if (publicPaths.includes(to.path) || publicPrefixes.some(p => to.path.startsWith(p))) {
            next();
        } else {
            next('/'); // إعادة توجيه لأي صفحة أخرى
        }
        return;
    }

    // السماح بالانتقال لأي صفحة أخرى
    next();
});

// Session-validity heartbeat (Feature: instant kick-out on revoked session) —
// centrally started only while the authenticated student app is the active
// area (meta.requiresAuth: contents/exams/subscriptions/notifications/…),
// stopped everywhere else (home, login, reservation, public pages). No
// individual page implements its own check — this single hook covers all of
// them, including any future route tagged requiresAuth.
router.afterEach((to) => {
    if (to.matched.some(record => record.meta.requiresAuth)) {
        AuthMonitor.startHeartbeat();
    } else {
        AuthMonitor.stopHeartbeat();
    }
});

export default router;
